import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import NotificationApi from "@apis/notification";
import { Bell, Clock, AlertCircle, Info, CheckCircle } from "lucide-react";

const Container = styled.div`
  background: #ffffff;
  border-radius: 20px;
  border: 1px solid #f1f5f9;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.02);
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
  border-bottom: 1px dashed #e2e8f0;
`;

const Title = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;

  svg {
    color: #ff8c00;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NotificationItem = styled.div<{ unread?: boolean }>`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
  background: ${(props) => (props.unread ? "#fffaf5" : "#ffffff")};
  border: 1px solid ${(props) => (props.unread ? "#ffedd5" : "#f1f5f9")};
  border-radius: 16px;
  transition: all 0.2s ease;
  box-shadow: ${(props) => (props.unread ? "0 4px 12px rgba(255, 140, 0, 0.05)" : "none")};

  &::before {
    content: '';
    position: absolute;
    left: -1px;
    top: 50%;
    transform: translateY(-50%);
    width: 4px;
    height: 60%;
    border-radius: 0 4px 4px 0;
    background: #ff8c00;
    opacity: ${(props) => (props.unread ? 1 : 0)};
    transition: opacity 0.2s;
  }

  &:hover {
    background: ${(props) => (props.unread ? "#fffaf5" : "#f8fafc")};
    border-color: ${(props) => (props.unread ? "#fed7aa" : "#e2e8f0")};
    transform: translateY(-2px);
  }
`;

const IconWrapper = styled.div<{ unread?: boolean }>`
  min-width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => (props.unread ? "#ffedd5" : "#f1f5f9")};
  color: ${(props) => (props.unread ? "#ff8c00" : "#64748b")};
`;

const Content = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ItemTitle = styled.div<{ unread?: boolean }>`
  font-size: 14px;
  font-weight: ${(props) => (props.unread ? 700 : 600)};
  color: #0f172a;
`;

const Message = styled.div`
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 4px;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const EmptyState = styled.div`
  padding: 40px 0;
  text-align: center;
  color: #94a3b8;
  font-size: 14px;
  font-weight: 500;
  background: #f8fafc;
  border-radius: 16px;
  border: 1px dashed #cbd5e1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  svg {
    color: #cbd5e1;
    width: 32px;
    height: 32px;
  }
`;

interface RecentNotificationsProps {
  userId?: number;
  role?: string;
}

const RecentNotifications: React.FC<RecentNotificationsProps> = ({
  userId,
  role,
}) => {
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotifs = async () => {
      try {
        setLoading(true);
        const res = await NotificationApi.list({
          params: {
            limit: 5,
            page: 1,
            ...(userId && { userId }),
            ...(role && { role }),
          },
          showErr: false,
        });

        let notifs = [];
        if (res.items) {
          notifs = res.items;
        } else if (res?.data?.data) {
          notifs = res.data.data;
        } else if (Array.isArray(res)) {
          notifs = res;
        }

        setNotifications(notifs.slice(0, 5));
      } catch (error) {
        console.error("Failed to fetch recent notifications", error);
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if we have user context or if it's the generic overview
    fetchNotifs();
  }, [userId, role]);

  const getIcon = (type: string, isRead: boolean) => {
    switch (type?.toLowerCase()) {
      case "success":
      case "completed":
        return <CheckCircle size={20} />;
      case "warning":
      case "failed":
        return <AlertCircle size={20} />;
      default:
        return <Bell size={20} />;
    }
  };

  const getRelativeTime = (dateString: string) => {
    if (!dateString) return "Just now";
    const date: any = new Date(dateString);
    const now: any = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    if (minutes > 0) return `${minutes}m ago`;
    return "Just now";
  };

  return (
    <Container>
      <Header>
        <Title>
          <Bell size={20} />
          Recent Notifications
        </Title>
      </Header>

      {loading ? (
        <List>
          {[1, 2, 3].map((i) => (
            <NotificationItem key={i}>
              <IconWrapper>
                <Bell size={20} opacity={0.2} />
              </IconWrapper>
              <Content>
                <div
                  style={{
                    height: 16,
                    width: "120px",
                    background: "#f1f5f9",
                    borderRadius: 4,
                  }}
                />
                <div
                  style={{
                    height: 12,
                    width: "100%",
                    background: "#f1f5f9",
                    borderRadius: 4,
                    marginTop: 4,
                  }}
                />
              </Content>
            </NotificationItem>
          ))}
        </List>
      ) : notifications.length > 0 ? (
        <List>
          {notifications.map((notif: any) => (
            <NotificationItem
              key={notif.id || Math.random()}
              unread={!notif.isRead}
            >
              <IconWrapper unread={!notif.isRead}>
                {getIcon(notif.type, notif.isRead)}
              </IconWrapper>
              <Content>
                <ItemTitle unread={!notif.isRead}>
                  {notif.title || "Notification"}
                </ItemTitle>
                <Message>{notif.message || notif.content || ""}</Message>
                <Meta>
                  <Clock />
                  {getRelativeTime(notif.createdAt)}
                </Meta>
              </Content>
            </NotificationItem>
          ))}
        </List>
      ) : (
        <EmptyState>
          <Bell size={32} />
          No recent notifications found.
        </EmptyState>
      )}
    </Container>
  );
};

export default RecentNotifications;
